import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Link, router } from '@inertiajs/react'
import { Plus, Eye, Edit, Trash2, Camera, Users, TrendingUp } from 'lucide-react'

interface Contest {
  id: number
  title: string
  theme: string
  status: string
  status_label: string
  community_name: string
  published: boolean
  active: boolean
  submission_start_date: string
  submission_end_date: string
  voting_end_date: string
  total_submissions: number
  created_at: string
}

interface Stats {
  total_contests: number
  published_contests: number
  active_contests: number
  total_submissions: number
}

interface IndexProps {
  contests: Contest[]
  stats: Stats
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submissions_open: 'bg-green-100 text-green-800',
  voting_open: 'bg-blue-100 text-blue-800',
  closed: 'bg-red-100 text-red-800',
  winners_declared: 'bg-purple-100 text-purple-800'
}

export default function Index({ contests, stats }: IndexProps) {
  const handleDelete = (contest: Contest) => {
    if (confirm(`Supprimer "${contest.title}" ?`)) {
      router.delete(`/admin/photo_contests/${contest.id}`)
    }
  }

  return (
    <AdminLayout title="Concours Photo" breadcrumbs={[{ label: 'Concours Photo' }]}>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray mb-1">Total Concours</p>
              <p className="text-2xl font-bold">{stats.total_contests}</p>
            </div>
            <Camera className="text-gray-300" size={32} />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray mb-1">Publiés</p>
              <p className="text-2xl font-bold text-tessan-primary">{stats.published_contests}</p>
            </div>
            <TrendingUp className="text-tessan-primary/30" size={32} />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray mb-1">Actifs</p>
              <p className="text-2xl font-bold text-green-600">{stats.active_contests}</p>
            </div>
            <Users className="text-green-300" size={32} />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray mb-1">Total Soumissions</p>
              <p className="text-2xl font-bold">{stats.total_submissions}</p>
            </div>
            <Camera className="text-gray-300" size={32} />
          </div>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex justify-end mb-6">
        <Link href="/admin/photo_contests/new">
          <Button className="flex items-center gap-2">
            <Plus size={18} />
            Nouveau Concours
          </Button>
        </Link>
      </div>

      {/* Contests List */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Titre</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Thème</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Statut</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Communauté</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Soumissions</th>
                <th className="text-right px-4 py-3 font-medium text-text-gray">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {contests.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-text-gray">
                    <Camera size={32} className="mx-auto mb-2 text-gray-300" />
                    Aucun concours
                  </td>
                </tr>
              ) : (
                contests.map((contest) => (
                  <tr key={contest.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-text-dark">{contest.title}</td>
                    <td className="px-4 py-3 text-sm text-text-gray">{contest.theme || '-'}</td>
                    <td className="px-4 py-3">
                      <Badge className={statusColors[contest.status]}>{contest.status_label}</Badge>
                    </td>
                    <td className="px-4 py-3 text-sm">{contest.community_name}</td>
                    <td className="px-4 py-3 font-medium">{contest.total_submissions}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link href={`/admin/photo_contests/${contest.id}`}>
                          <Button variant="secondary" className="p-2">
                            <Eye size={16} />
                          </Button>
                        </Link>
                        <Link href={`/admin/photo_contests/${contest.id}/edit`}>
                          <Button variant="secondary" className="p-2">
                            <Edit size={16} />
                          </Button>
                        </Link>
                        <Button
                          variant="secondary"
                          className="p-2 text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(contest)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminLayout>
  )
}
